// v1.20.0 · CRM › Asistente › Resultados: pagado vs. orgánico, por nicho y por pieza (evolución, leads, retorno y consejos).
// v1.20.1 · indicadores con el cambio frente al periodo anterior, gráficos de evolución diaria y embudo.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, ExternalLink, CheckCircle2, AlertCircle, XCircle, Clock, Sparkles, ArrowUp, ArrowDown } from 'lucide-react';
import { api, errMsg, btn, Empty, fdate, fdatetime, money } from './ui';
import { C, Card, LineChart, BarChart, HBars } from './Charts';

const PERIODS = [[7, '7 días'], [30, '30 días'], [90, '90 días']];
// Estado: siempre con ícono y texto, nunca solo color
const SIGNAL = {
  bien: [CheckCircle2, 'Funciona', 'bg-green-50 text-green-800 border-green-200'],
  revisar: [AlertCircle, 'Revisar', 'bg-amber-50 text-amber-800 border-amber-200'],
  mal: [XCircle, 'Pausar o cambiar', 'bg-red-50 text-red-800 border-red-200'],
  esperar: [Clock, 'Esperando datos', 'bg-gray-50 text-gray-700 border-gray-200'],
};
const n0 = (v) => Number(v || 0).toLocaleString('es-EC', { maximumFractionDigits: 0 });
const pct = (v) => `${Number(v || 0).toLocaleString('es-EC', { maximumFractionDigits: 2 })}%`;
const cpl = (v) => (v == null ? '—' : money(v));
const roi = (v) => (v == null ? '—' : `${Number(v).toLocaleString('es-EC', { maximumFractionDigits: 2 })}×`);

function Signal({ s }) {
  const [Icon, label, cls] = SIGNAL[s] || SIGNAL.esperar;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid="res-signal"><Icon size={13} />{label}</span>;
}

/** Evolución del alcance acumulado por día: una sola serie, sin ejes, con el valor de cada punto al pasar el mouse. */
function Spark({ data, k = 'alcance' }) {
  const pts = (data || []).map((d) => ({ x: d.dia, y: Number(d[k] || 0) }));
  if (pts.length < 2) return <span className="text-xs text-gray-400">Sin evolución aún</span>;
  const W = 120, H = 32, max = Math.max(...pts.map((p) => p.y), 1);
  const xy = pts.map((p, i) => [4 + (i * (W - 8)) / (pts.length - 1), H - 4 - (p.y / max) * (H - 8)]);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Evolución del ${k}: de ${n0(pts[0].y)} a ${n0(pts[pts.length - 1].y)}`}>
      <polyline points={xy.map((p) => p.join(',')).join(' ')} fill="none" stroke="#2F3D8E" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {xy.map(([x, y], i) => (
        <g key={pts[i].x}>
          <circle cx={x} cy={y} r="8" fill="transparent"><title>{`${fdate(pts[i].x)}: ${n0(pts[i].y)} de ${k}`}</title></circle>
          {i === xy.length - 1 && <circle cx={x} cy={y} r="3" fill="#2F3D8E" stroke="#fff" strokeWidth="2" />}
        </g>
      ))}
    </svg>
  );
}

const ROWS = [
  ['piezas', 'Piezas', n0], ['alcance', 'Alcance', n0], ['impresiones', 'Impresiones', n0], ['clics', 'Clics', n0], ['ctr', 'CTR', pct],
  ['interacciones', 'Interacciones', n0], ['gasto', 'Gasto', money], ['leads', 'Leads', n0], ['reservas', 'Reuniones reservadas', n0],
  ['costo_por_lead', 'Costo por lead', cpl], ['ganado', 'Ventas ganadas', money], ['retorno', 'Retorno (ventas / gasto)', roi],
];

function Compare({ r }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="res-compare">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600"><tr><th className="px-3 py-2 text-left font-semibold">Acumulado de las piezas<span className="block text-[10px] font-normal text-gray-500">desde que se publicó cada una</span></th><th className="px-3 py-2 text-right font-semibold">Pagado</th><th className="px-3 py-2 text-right font-semibold">Orgánico</th></tr></thead>
        <tbody>
          {ROWS.map(([k, l, f]) => (
            <tr key={k} className="border-t border-gray-100">
              <td className="px-3 py-1.5 text-gray-600">{l}</td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-[#0F2A3A]">{f(r.pagado[k])}</td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-[#0F2A3A]">{k === 'gasto' || k === 'costo_por_lead' || k === 'retorno' || (['impresiones', 'ctr'].includes(k) && !r['orgánico'].impresiones) ? '—' : f(r['orgánico'][k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Niches({ items }) {
  if (!items.length) return null;
  return (
    <section className="min-w-0 space-y-2">
      <h3 className="text-sm font-bold text-[#0F2A3A]">Por nicho <span className="font-normal text-gray-500">· acumulado</span></h3>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white" data-testid="res-niches">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>{['Nicho', 'Alcance pagado', 'Alcance orgánico', 'CTR pagado', 'Gasto', 'Leads (de anuncios)', 'Reuniones', 'Costo por lead'].map((h, i) => <th key={h} className={`px-3 py-2 font-semibold ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((n) => (
              <tr key={n.nicho} className="border-t border-gray-100">
                <td className="whitespace-nowrap px-3 py-1.5 font-semibold text-[#0F2A3A]">{n.nicho}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{n0(n.pagado.alcance)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{n0(n['orgánico'].alcance)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{pct(n.pagado.ctr)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(n.pagado.gasto)}</td>
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{n0(n.leads_crm)} <span className="font-normal text-gray-500">({n0(n.leads_pagados)})</span></td>
                <td className="px-3 py-1.5 text-right tabular-nums">{n0(n.reservas_crm)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{cpl(n.costo_por_lead)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Piece({ p }) {
  const stats = p.pagado
    ? [['Alcance', n0(p.alcance)], ['Clics', n0(p.clics)], ['CTR', pct(p.ctr)], ['Gasto', money(p.gasto)], ['Leads', n0(p.leads)], ['Costo por lead', cpl(p.costo_por_lead)]]
    : [['Alcance', n0(p.alcance)], ['Interacciones', n0(p.interacciones)], ['Clics', n0(p.clics)], ['Vistas', n0(p.vistas)], ['Leads', n0(p.leads)], ['Reuniones', n0(p.reservas)]];
  return (
    <li className="space-y-2 rounded-xl border border-gray-200 bg-white p-3" data-testid="res-piece">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[#0F2A3A]" title={p.titulo}>{p.titulo}</p>
          <p className="text-xs text-gray-500">{p.pagado ? 'Pagado · anuncio' : 'Orgánico · publicación'} · {p.nicho} · {fdate(p.publicado)}{p.estado_anuncio ? ` · ${p.estado_anuncio === 'ACTIVE' ? 'activo' : 'pausado'}` : ''}</p>
        </div>
        <Signal s={p['señal']} />
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <dl className="grid flex-1 grid-cols-3 gap-2 sm:grid-cols-6">
          {stats.map(([l, v]) => <div key={l}><dt className="text-[10px] text-gray-500">{l}</dt><dd className="text-sm font-bold tabular-nums text-[#0F2A3A]">{v}</dd></div>)}
        </dl>
        <Spark data={p.evolucion} />
      </div>
      <p className="text-xs text-gray-700">{p.consejo}</p>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-gray-400">
        <span>{p.metricas_al ? `Métricas ${fdatetime(p.metricas_al)}` : 'Sin métricas todavía'}{p.error_metricas ? ` · Error al leer: ${p.error_metricas}` : ''}</span>
        {p.enlace && <a href={p.enlace} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#2F3D8E]">Ver en Facebook <ExternalLink size={11} /></a>}
      </div>
    </li>
  );
}

const KFMT = { n: n0, usd: (v) => money(v), pct, x: roi };

function Kpi({ k }) {
  const f = KFMT[k.formato] || n0;
  const ch = k.cambio_pct;
  const good = ch != null && ch !== 0 && ((ch > 0) === (k.mejor !== 'baja'));
  return (
    <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-3" title={k.ayuda || undefined} data-testid={`kpi-${k.clave}`}>
      <p className="truncate text-xs text-gray-500">{k.nombre}</p>
      <p className="text-xl font-bold tabular-nums text-[#0F2A3A]">{k.valor == null ? '—' : f(k.valor)}</p>
      {ch == null ? <p className="text-[11px] text-gray-400">{k.anterior == null ? 'Sin periodo anterior' : `Antes: ${f(k.anterior)}`}</p> : (
        <p className={`flex items-center gap-0.5 text-[11px] font-semibold ${ch === 0 ? 'text-gray-500' : good ? 'text-green-700' : 'text-red-700'}`}>
          {ch > 0 ? <ArrowUp size={12} /> : ch < 0 ? <ArrowDown size={12} /> : null}
          {`${ch > 0 ? '+' : ''}${pct(ch)} vs. periodo anterior`}<span className="sr-only">{good ? ' (mejora)' : ch === 0 ? '' : ' (empeora)'}</span>
        </p>
      )}
    </div>
  );
}

function Evolution({ r }) {
  const s = r.serie || [];
  if (!s.length) return null;
  const labels = s.map((x) => x.dia);
  const col = (side, k) => s.map((x) => x[side][k]);
  const tot = (vals) => vals.reduce((a, v) => a + (v || 0), 0);
  const line = (k) => [
    { name: 'Pagado', color: C.pagado, values: col('pagado', k), total: tot(col('pagado', k)) },
    { name: 'Orgánico', color: C.organico, values: col('orgánico', k), total: tot(col('orgánico', k)) }];
  const leads = ['pagado', 'orgánico', 'otros'].map((k, i) => {
    const values = s.map((x) => x.leads[k]);
    return { name: ['De anuncios', 'Orgánico', 'WhatsApp y sitio'][i], color: [C.pagado, C.organico, C.otros][i], values, total: tot(values) };
  });
  let g = 0, l = 0;
  const cpl = s.map((x) => { g += x.pagado.gasto; l += x.leads.pagado; return l ? g / l : null; });
  const steps = (r.embudo || []).map((e) => ({ label: e.etapa, value: e.valor, note: e.paso_pct == null ? '' : `${pct(e.paso_pct)} de la etapa anterior` }));
  return (
    <section className="space-y-2" data-testid="res-evolution">
      <h3 className="text-sm font-bold text-[#0F2A3A]">Evolución diaria</h3>
      <div className="grid gap-3 lg:grid-cols-2">
        <Card title="Personas alcanzadas por día" sub="Alcance nuevo de cada día"><LineChart labels={labels} series={line('alcance')} fmt={n0} testId="chart-alcance" /></Card>
        <Card title="Clics por día" sub="Clics al enlace (anuncios) y clics en las publicaciones"><LineChart labels={labels} series={line('clics')} fmt={n0} testId="chart-clics" /></Card>
        <Card title="Leads por día" sub="Según de dónde llegó cada contacto"><BarChart labels={labels} series={leads} fmt={n0} integer testId="chart-leads" /></Card>
        <Card title="Gasto en anuncios por día"><BarChart labels={labels} series={[{ name: 'Gasto', color: C.pagado, values: col('pagado', 'gasto') }]} fmt={(v) => money(v)} testId="chart-gasto" /></Card>
        <Card title="Costo por lead acumulado" sub="Gasto acumulado ÷ leads de anuncios acumulados; baja cuando la campaña mejora">
          <LineChart labels={labels} series={[{ name: 'Costo por lead', color: C.pagado, values: cpl }]} fmt={(v) => money(v)} testId="chart-cpl" />
        </Card>
        <Card title="Embudo del periodo" sub="Dónde se pierde la gente: si cae entre clics y leads, mejore la página de reservas">
          <HBars rows={steps} fmt={n0} testId="chart-funnel" />
        </Card>
      </div>
      <p className="text-[11px] text-gray-500">Los indicadores de arriba y estos gráficos cuentan solo lo ocurrido en el periodo; las tablas de abajo, todo lo acumulado por cada pieza. El día de hoy va en curso. La primera lectura de cada pieza se reparte entre los días desde que se publicó; las siguientes se cuentan el día en que ocurrieron (hora de Ecuador).</p>
    </section>
  );
}

export default function Results() {
  const [days, setDays] = useState(30);
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const load = useCallback(async () => {
    setR(null);
    try { setR(await api.get('/ai/results', { days })); } catch (e) { toast.error(errMsg(e)); setR({ error: true }); }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setBusy(true);
    try {
      const x = await api.post('/ai/results/refresh');
      toast.success(`Métricas actualizadas: ${x.actualizadas}${x.errores?.length ? ` · ${x.errores.length} con error` : ''}`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pieces = (r?.piezas || []).filter((p) => filter === 'all' || (filter === 'paid' ? p.pagado : !p.pagado));
  return (
    <div className="space-y-4" data-testid="crm-results">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map(([d, l]) => (
          <button key={d} onClick={() => setDays(d)} aria-pressed={days === d} className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${days === d ? 'border-[#2F3D8E] bg-[#2F3D8E] text-white' : 'border-gray-300 bg-white text-gray-700'}`}>{l}</button>
        ))}
        <button onClick={refresh} disabled={busy} className={`${btn.ghost} sm:ml-auto`} data-testid="res-refresh"><RefreshCw size={15} className={busy ? 'animate-spin' : ''} />{busy ? 'Actualizando…' : 'Actualizar métricas'}</button>
      </div>
      {!r ? <p className="py-10 text-center text-sm text-gray-500">Cargando…</p>
        : r.error ? <Empty title="No se pudieron cargar los resultados">Intente de nuevo en unos minutos.</Empty>
        : !r.piezas.length && !(r.serie || []).some((x) => x.leads.pagado + x.leads['orgánico'] + x.leads.otros) ? <Empty title="Aún no hay publicaciones en este periodo">Cuando se publiquen anuncios o publicaciones desde el CRM, sus métricas aparecerán aquí (se actualizan solas cada 6 horas).</Empty>
        : (
          <>
            {r.consejos.length > 0 && (
              <section className="space-y-1.5 rounded-xl border border-violet-200 bg-violet-50/60 p-3" data-testid="res-advice">
                <h3 className="flex items-center gap-1.5 text-sm font-bold text-violet-900"><Sparkles size={15} />Qué hacer ahora</h3>
                <ul className="list-disc space-y-1 pl-5 text-sm text-violet-950">{r.consejos.map((t) => <li key={t}>{t}</li>)}</ul>
                <p className="text-[11px] text-violet-800">Son reglas simples sobre los números; nada se pausa ni se cambia solo.</p>
              </section>
            )}
            {r.kpis?.length > 0 && <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="res-kpis">{r.kpis.map((k) => <Kpi key={k.clave} k={k} />)}</div>}
            <Evolution r={r} />
            <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_1fr]">
              <Compare r={r.resumen} />
              <Niches items={r.nichos} />
            </div>
            <section className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-bold text-[#0F2A3A]">Por pieza</h3>
                {[['all', 'Todas'], ['paid', 'Pagadas'], ['org', 'Orgánicas']].map(([k, l]) => (
                  <button key={k} onClick={() => setFilter(k)} aria-pressed={filter === k} className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${filter === k ? 'border-[#0F2A3A] bg-[#0F2A3A] text-white' : 'border-gray-300 bg-white text-gray-700'}`}>{l}</button>
                ))}
              </div>
              <ul className="space-y-2">{pieces.map((p) => <Piece key={p.id} p={p} />)}</ul>
            </section>
            <p className="text-[11px] text-gray-500">Leads por pieza: se cuentan los contactos que llegaron por el enlace (utm) o por el anuncio a WhatsApp o el formulario de Facebook creados desde el CRM. Lo publicado antes de la versión 1.19 no trae esa marca. Generado {fdatetime(r.generado)}.</p>
          </>
        )}
    </div>
  );
}
